import { Injectable } from '@angular/core';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export interface UiBackendErrorField {
  field: string;
  messages: string[];
}

export interface UiBackendError {
  message: string;
  title?: string;
  fields: UiBackendErrorField[];
  status?: number;
  raw?: unknown;
}

@Injectable({ providedIn: 'root' })
export class ApiErrorAdapterService {
  toUiError(error: unknown, fallback: string): UiBackendError {
    const result: UiBackendError = {
      message: fallback,
      fields: [],
      raw: error,
    };

    if (typeof error !== 'object' || error === null) {
      return result;
    }

    const status = 'status' in error && typeof error.status === 'number' ? error.status : undefined;
    result.status = status;

    const backendError = 'error' in error ? error.error : null;

    if (typeof backendError === 'string' && backendError.trim()) {
      result.message = backendError;
      return result;
    }

    if (typeof backendError === 'object' && backendError !== null) {
      const title = 'title' in backendError && typeof backendError.title === 'string' ? backendError.title.trim() : '';
      const detail =
        'detail' in backendError && typeof backendError.detail === 'string' ? backendError.detail.trim() : '';
      const message =
        'message' in backendError && typeof backendError.message === 'string' ? backendError.message.trim() : '';

      if (title) {
        result.title = title;
      }

      if (detail) {
        result.message = detail;
      } else if (message) {
        result.message = message;
      } else if (title) {
        result.message = title;
      }

      const validationErrors = 'errors' in backendError ? backendError.errors : null;
      if (typeof validationErrors === 'object' && validationErrors !== null) {
        result.fields = Object.entries(validationErrors)
          .map(([field, value]) => {
            if (Array.isArray(value)) {
              const messages = value.filter(
                (item): item is string => typeof item === 'string' && item.trim().length > 0,
              );
              return { field, messages };
            }

            if (typeof value === 'string' && value.trim()) {
              return { field, messages: [value] };
            }

            return { field, messages: [] };
          })
          .filter((entry) => entry.messages.length > 0);

        if (result.fields.length > 0 && !detail && !message) {
          result.message = 'Please review the highlighted validation errors.';
        }
      }
    }

    const topLevelMessage = 'message' in error && typeof error.message === 'string' ? error.message.trim() : '';
    if (topLevelMessage && result.message === fallback) {
      result.message = topLevelMessage;
    }

    return result;
  }
}

@Component({
  selector: 'mlw-api-error',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (error()) {
      <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        @if (error()!.title) {
          <div class="mb-1 font-semibold">{{ error()!.title }}</div>
        }

        <div>{{ error()!.message }}</div>

        @if (error()!.fields.length > 0) {
          <ul class="mt-3 list-disc pl-5">
            @for (fieldError of error()!.fields; track fieldError.field) {
              <li>
                <span class="font-medium">{{ fieldError.field }}:</span>
                {{ fieldError.messages.join(', ') }}
              </li>
            }
          </ul>
        }
      </div>
    }
  `,
})
export class ApiErrorComponent {
  readonly error = input<UiBackendError | null>(null);
}
